<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import {
  Bell,
  Blocks,
  BookOpen,
  Briefcase,
  ChevronDown,
  CreditCard,
  FileCheck,
  FileText,
  Film,
  Image,
  Layers,
  LayoutDashboard,
  MessageSquare,
  Package,
  Palette,
  QrCode,
  Search,
  Send,
  Settings,
  Share2,
  Shield,
  ShieldCheck,
  ShoppingBag,
  Sparkles,
  Store,
  Ticket,
  Users,
  Video,
  X
} from '@lucide/vue'
import type { AdminLayoutMenuGroup, AdminLayoutMenuItem } from '../../types'

function resolveGroupIcon(group: AdminLayoutMenuGroup) {
  if (group.icon && typeof group.icon !== 'string') {
    return group.icon
  }
  const name = `${group.label} ${group.id}`.toLowerCase()
  if (name.includes('系统') || name.includes('工作台') || name.includes('dashboard')) return LayoutDashboard
  if (name.includes('通知') || name.includes('消息') || name.includes('notice') || name.includes('message')) return Bell
  if (name.includes('权限') || name.includes('安全') || name.includes('auth') || name.includes('permission')) return Shield
  if (name.includes('审核') || name.includes('audit')) return ShieldCheck
  if (name.includes('cms') || name.includes('内容') || name.includes('文章') || name.includes('content')) return FileText
  if (name.includes('客户') || name.includes('用户') || name.includes('会员') || name.includes('user') || name.includes('member')) return Users
  if (name.includes('装修') || name.includes('主题') || name.includes('decoration')) return Palette
  if (name.includes('分销') || name.includes('推广') || name.includes('share') || name.includes('distrib')) return Share2
  if (name.includes('商城') || name.includes('商品') || name.includes('电商') || name.includes('mall') || name.includes('product')) return ShoppingBag
  if (name.includes('erp') || name.includes('进销存') || name.includes('库存') || name.includes('stock')) return Package
  if (name.includes('公信') || name.includes('公证') || name.includes('区块链') || name.includes('cert')) return FileCheck
  if (name.includes('财务') || name.includes('结算') || name.includes('支付') || name.includes('finance') || name.includes('pay')) return CreditCard
  if (name.includes('国学') || name.includes('图书') || name.includes('知识') || name.includes('book')) return BookOpen
  if (name.includes('即时通讯') || name.includes('聊天') || name.includes('im') || name.includes('chat')) return MessageSquare
  if (name.includes('直播') || name.includes('live') || name.includes('broadcast')) return Video
  if (name.includes('素材') || name.includes('资源') || name.includes('媒体') || name.includes('material') || name.includes('asset') || name.includes('image')) return Image
  if (name.includes('协同') || name.includes('办公') || name.includes('oa') || name.includes('workflow') || name.includes('office')) return Briefcase
  if (name.includes('推送') || name.includes('push') || name.includes('触达') || name.includes('短信')) return Send
  if (name.includes('二维码') || name.includes('qr') || name.includes('qrcode')) return QrCode
  if (name.includes('选品') || name.includes('供应链') || name.includes('货源') || name.includes('market') || name.includes('supply')) return Store
  if (name.includes('票务') || name.includes('门票') || name.includes('票券') || name.includes('ticket')) return Ticket
  if (name.includes('短剧') || name.includes('微短剧') || name.includes('剧场') || name.includes('drama')) return Film
  if (name.includes('设置') || name.includes('配置') || name.includes('setting') || name.includes('config')) return Settings
  if (name.includes('ai') || name.includes('智能') || name.includes('模型')) return Sparkles

  if (typeof group.icon === 'string' && group.icon.trim().length <= 2) {
    return group.icon.trim()
  }
  return Blocks
}

const props = withDefaults(
  defineProps<{
    menuGroups: AdminLayoutMenuGroup[]
    sidebarCollapsed?: boolean
    mobileMenuOpen?: boolean
    activeWorkspaceTabId?: string
  }>(),
  {
    sidebarCollapsed: false,
    mobileMenuOpen: false,
    activeWorkspaceTabId: ''
  }
)

const emit = defineEmits<{
  'menu-item-click': [item: AdminLayoutMenuItem, group: AdminLayoutMenuGroup]
  'update:sidebarCollapsed': [collapsed: boolean]
  'update:mobileMenuOpen': [open: boolean]
}>()

const navigationQuery = ref('')
const navigationSearchRef = ref<HTMLInputElement | null>(null)
const openGroups = ref<Record<string, boolean>>({})
const openMenuItems = ref<Record<string, boolean>>({})

const normalizedNavigationQuery = computed(() => navigationQuery.value.trim().toLocaleLowerCase())

const visibleMenuGroups = computed<AdminLayoutMenuGroup[]>(() => {
  const groups = props.menuGroups.filter((group) => group.items.length > 0)
  const query = normalizedNavigationQuery.value
  if (!query) return groups

  return groups
    .map((group) => {
      const groupMatches = `${group.label} ${group.subtitle ?? ''}`.toLocaleLowerCase().includes(query)
      if (groupMatches) return group

      const includedIndexes = new Set<number>()
      group.items.forEach((item, index) => {
        const itemText = `${item.label} ${item.subtitle ?? ''}`.toLocaleLowerCase()
        if (!itemText.includes(query)) return

        includedIndexes.add(index)
        const itemDepth = item.depth ?? 0
        for (let depth = itemDepth - 1; depth >= 0; depth -= 1) {
          for (let ancestorIndex = index - 1; ancestorIndex >= 0; ancestorIndex -= 1) {
            if ((group.items[ancestorIndex]?.depth ?? 0) === depth) {
              includedIndexes.add(ancestorIndex)
              break
            }
          }
        }
      })

      return {
        ...group,
        items: group.items.filter((_, index) => includedIndexes.has(index))
      }
    })
    .filter((group) => group.items.length > 0)
})

const visibleNavigationItemCount = computed(() =>
  visibleMenuGroups.value.reduce((total, group) => total + group.items.length, 0)
)

function isGroupOpen(groupId: string) {
  if (normalizedNavigationQuery.value) return true
  return openGroups.value[groupId] ?? true
}

function toggleGroup(groupId: string) {
  openGroups.value = {
    ...openGroups.value,
    [groupId]: !isGroupOpen(groupId)
  }
}

function handleGroupClick(groupId: string) {
  if (props.sidebarCollapsed) {
    emit('update:sidebarCollapsed', false)
    openGroups.value = { ...openGroups.value, [groupId]: true }
    return
  }
  toggleGroup(groupId)
}

async function focusNavigationSearch() {
  emit('update:sidebarCollapsed', false)
  emit('update:mobileMenuOpen', true)
  await nextTick()
  navigationSearchRef.value?.focus()
}

function clearNavigationSearch() {
  navigationQuery.value = ''
  navigationSearchRef.value?.focus()
}

function groupActive(group: AdminLayoutMenuGroup) {
  return group.items.some((item) => item.active)
}

function childItemsFor(group: AdminLayoutMenuGroup, item: AdminLayoutMenuItem) {
  const itemIndex = group.items.indexOf(item)
  if (itemIndex < 0) return []
  const depth = item.depth ?? 0
  const children: AdminLayoutMenuItem[] = []
  for (const candidate of group.items.slice(itemIndex + 1)) {
    const candidateDepth = candidate.depth ?? 0
    if (candidateDepth <= depth) break
    if (candidateDepth === depth + 1) {
      children.push(candidate)
    }
  }
  return children
}

function menuItemExpandable(group: AdminLayoutMenuGroup, item: AdminLayoutMenuItem) {
  return childItemsFor(group, item).length > 0
}

function descendantItemsFor(group: AdminLayoutMenuGroup, item: AdminLayoutMenuItem) {
  const itemIndex = group.items.indexOf(item)
  if (itemIndex < 0) return []
  const depth = item.depth ?? 0
  const descendants: AdminLayoutMenuItem[] = []
  for (const candidate of group.items.slice(itemIndex + 1)) {
    const candidateDepth = candidate.depth ?? 0
    if (candidateDepth <= depth) break
    descendants.push(candidate)
  }
  return descendants
}

function menuItemActive(group: AdminLayoutMenuGroup, item: AdminLayoutMenuItem) {
  if (item.active) return true
  return descendantItemsFor(group, item).some((candidate) => candidate.active)
}

function menuItemOpen(itemId: string) {
  return openMenuItems.value[itemId] ?? true
}

function toggleMenuItem(itemId: string) {
  openMenuItems.value = {
    ...openMenuItems.value,
    [itemId]: !menuItemOpen(itemId)
  }
}

function isMenuItemVisible(group: AdminLayoutMenuGroup, item: AdminLayoutMenuItem) {
  if (normalizedNavigationQuery.value) return true
  const itemIndex = group.items.indexOf(item)
  if (itemIndex < 0) return true
  const depth = item.depth ?? 0
  if (depth === 0) return true

  for (let currentDepth = depth - 1; currentDepth >= 0; currentDepth -= 1) {
    const parent = [...group.items.slice(0, itemIndex)]
      .reverse()
      .find((candidate) => (candidate.depth ?? 0) === currentDepth)
    if (!parent) continue
    if (!menuItemOpen(parent.id)) return false
  }
  return true
}

function itemBadgeText(item: AdminLayoutMenuItem) {
  if (item.badge === undefined || item.badge === null || item.badge === '') return ''
  return String(item.badge)
}

function handleMenuClick(item: AdminLayoutMenuItem, group: AdminLayoutMenuGroup) {
  if (menuItemExpandable(group, item)) {
    toggleMenuItem(item.id)
    return
  }
  if (item.disabled) return
  emit('menu-item-click', item, group)
  emit('update:mobileMenuOpen', false)
}

watch(
  visibleMenuGroups,
  (groups) => {
    const nextOpenGroups: Record<string, boolean> = {}
    const nextOpenMenuItems: Record<string, boolean> = {}
    for (const [groupIndex, group] of groups.entries()) {
      nextOpenGroups[group.id] = openGroups.value[group.id] ?? (groupActive(group) || groupIndex === 0)
      for (const item of group.items) {
        if (menuItemExpandable(group, item)) {
          nextOpenMenuItems[item.id] = openMenuItems.value[item.id] ?? true
        }
      }
    }
    openGroups.value = nextOpenGroups
    openMenuItems.value = nextOpenMenuItems
  },
  { immediate: true }
)

watch(
  () => props.activeWorkspaceTabId,
  () => {
    const activeGroup = props.menuGroups.find((group) => groupActive(group))
    if (activeGroup) {
      openGroups.value = { ...openGroups.value, [activeGroup.id]: true }
      const nextOpenMenuItems = { ...openMenuItems.value }
      for (const item of activeGroup.items) {
        if (menuItemExpandable(activeGroup, item) && menuItemActive(activeGroup, item)) {
          nextOpenMenuItems[item.id] = true
        }
      }
      openMenuItems.value = nextOpenMenuItems
    }
  },
  { immediate: true }
)
</script>

<template>
  <aside class="admin-shell__sidebar" :class="{ 'is-collapsed': sidebarCollapsed }">
    <div class="admin-shell__nav-search-wrap">
      <button
        v-if="sidebarCollapsed"
        type="button"
        class="admin-shell__nav-search-trigger"
        title="搜索菜单"
        aria-label="搜索菜单"
        @click="focusNavigationSearch"
      >
        <Search :size="18" :stroke-width="1.8" aria-hidden="true" />
      </button>
      <label v-else class="admin-shell__nav-search">
        <Search :size="16" :stroke-width="1.8" aria-hidden="true" />
        <input
          ref="navigationSearchRef"
          v-model="navigationQuery"
          type="search"
          placeholder="搜索菜单"
          aria-label="搜索菜单"
        />
        <button
          v-if="navigationQuery"
          type="button"
          aria-label="清空菜单搜索"
          title="清空搜索"
          @click="clearNavigationSearch"
        >
          <X :size="14" :stroke-width="2" aria-hidden="true" />
        </button>
      </label>
    </div>

    <nav class="admin-shell__nav" aria-label="后台菜单">
      <section v-for="group in visibleMenuGroups" :key="group.id" class="admin-shell__nav-group">
        <button
          type="button"
          class="admin-shell__nav-parent"
          :class="{ active: groupActive(group) }"
          :aria-expanded="isGroupOpen(group.id)"
          :title="sidebarCollapsed ? group.label : undefined"
          @click="handleGroupClick(group.id)"
        >
          <span class="admin-shell__nav-icon">
            <component
              :is="resolveGroupIcon(group)"
              v-if="typeof resolveGroupIcon(group) !== 'string'"
              :size="18"
              :stroke-width="1.8"
              aria-hidden="true"
            />
            <span v-else class="admin-shell__nav-char-icon">{{ resolveGroupIcon(group) }}</span>
          </span>
          <span class="admin-shell__nav-main">
            <span class="admin-shell__nav-title">{{ group.label }}</span>
            <small v-if="group.subtitle">{{ group.subtitle }}</small>
          </span>
          <ChevronDown
            class="admin-shell__nav-caret"
            :class="{ open: isGroupOpen(group.id) }"
            :size="15"
            :stroke-width="2"
            aria-hidden="true"
          />
        </button>

        <div v-show="isGroupOpen(group.id)" class="admin-shell__submenu">
          <button
            v-for="item in group.items"
            v-show="isMenuItemVisible(group, item)"
            :key="item.id"
            type="button"
            class="admin-shell__submenu-item"
            :class="{
              active: menuItemActive(group, item),
              expandable: menuItemExpandable(group, item),
              open: menuItemExpandable(group, item) && menuItemOpen(item.id)
            }"
            :aria-expanded="menuItemExpandable(group, item) ? menuItemOpen(item.id) : undefined"
            :aria-current="item.active ? 'page' : undefined"
            :disabled="item.disabled && !menuItemExpandable(group, item)"
            :style="{ '--menu-depth': String(item.depth ?? 0) }"
            :title="sidebarCollapsed ? `${group.label} / ${item.label}` : item.label"
            @click="handleMenuClick(item, group)"
          >
            <span>{{ item.label }}</span>
            <small v-if="itemBadgeText(item)">{{ itemBadgeText(item) }}</small>
            <ChevronDown
              v-else-if="menuItemExpandable(group, item)"
              class="admin-shell__submenu-caret"
              :size="14"
              :stroke-width="2"
              aria-hidden="true"
            />
          </button>
        </div>
      </section>

      <div
        v-if="normalizedNavigationQuery && visibleNavigationItemCount === 0"
        class="admin-shell__nav-empty"
      >
        <Search :size="18" :stroke-width="1.7" aria-hidden="true" />
        <span>没有匹配的菜单</span>
      </div>
    </nav>
  </aside>
</template>

<style scoped>
.admin-shell__sidebar {
  position: fixed;
  z-index: 120;
  top: var(--admin-shell-topbar-height, 50px);
  bottom: 0;
  left: 0;
  width: var(--admin-shell-sidebar-width, 230px);
  box-sizing: border-box;
  background: var(--rr-color-text-strong);
  overflow-x: hidden;
  overflow-y: auto;
  scrollbar-width: none;
  transition: width 0.24s ease, transform 0.24s ease;
}

.admin-shell__sidebar::-webkit-scrollbar {
  display: none;
}

.admin-shell__nav-search-wrap {
  padding: 10px 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.admin-shell__nav-search-trigger {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 32px;
  border: 0;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  margin: 0 auto;
}

.admin-shell__nav-search {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 32px;
  padding: 0 10px;
  border-radius: var(--rr-radius-control, 6px);
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.65);
  box-sizing: border-box;
  transition: all 140ms ease;
}

.admin-shell__nav-search:hover {
  border-color: rgba(255, 255, 255, 0.24);
  background: rgba(255, 255, 255, 0.09);
  color: rgba(255, 255, 255, 0.9);
}

.admin-shell__nav-search:focus-within {
  border-color: var(--rr-color-primary, #5a46e0);
  background: rgba(0, 0, 0, 0.35);
  box-shadow: 0 0 0 2px rgba(90, 70, 224, 0.28);
  color: #ffffff;
}

.admin-shell__nav-search input {
  flex: 1;
  min-width: 0;
  border: 0;
  background: transparent;
  color: #ffffff;
  font-size: 12px;
  outline: none;
  box-shadow: none;
}

.admin-shell__nav-search input::placeholder {
  color: rgba(255, 255, 255, 0.38);
}

.admin-shell__nav-search button {
  border: 0;
  background: transparent;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  padding: 0;
  display: grid;
  place-items: center;
}

.admin-shell__nav {
  display: grid;
  align-content: start;
  padding: 8px 0;
}

.admin-shell__nav-group {
  display: grid;
}

.admin-shell__nav-parent {
  position: relative;
  display: grid;
  grid-template-columns: 44px minmax(0, 1fr) 32px;
  align-items: center;
  width: 100%;
  height: 38px;
  border: 0;
  background: transparent;
  color: rgba(255, 255, 255, 0.72);
  cursor: pointer;
  padding: 0;
  text-align: left;
  transition: all 140ms ease;
  user-select: none;
}

.admin-shell__nav-parent:hover {
  color: #ffffff;
  background: rgba(255, 255, 255, 0.06);
}

.admin-shell__nav-parent.active {
  color: #ffffff;
  background: rgba(90, 70, 224, 0.16);
  font-weight: 600;
}

.admin-shell__nav-parent.active::before {
  content: '';
  position: absolute;
  left: 0;
  top: 7px;
  bottom: 7px;
  width: 3px;
  border-radius: 0 2px 2px 0;
  background: var(--rr-color-primary, #5a46e0);
}

.admin-shell__nav-icon {
  display: grid;
  place-items: center;
  color: inherit;
}

.admin-shell__nav-char-icon {
  display: grid;
  place-items: center;
  width: 22px;
  height: 22px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.12);
  color: #ffffff;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
}

.admin-shell__nav-main {
  display: flex;
  align-items: center;
  min-width: 0;
  gap: 6px;
}

.admin-shell__nav-title {
  font-size: 13px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.admin-shell__nav-main small {
  display: none;
}

.admin-shell__nav-caret {
  opacity: 0.35;
  color: rgba(255, 255, 255, 0.6);
  transition: all 180ms ease;
}

.admin-shell__nav-parent:hover .admin-shell__nav-caret,
.admin-shell__nav-parent.active .admin-shell__nav-caret {
  opacity: 0.8;
  color: #ffffff;
}

.admin-shell__nav-caret.open {
  transform: rotate(180deg);
  opacity: 0.9;
}

.admin-shell__submenu {
  display: flex;
  flex-direction: column;
}

.admin-shell__submenu-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: calc(100% - 16px);
  margin: 2px 8px;
  min-height: 38px;
  border: 0;
  border-radius: var(--rr-radius-control, 6px);
  background: transparent;
  color: rgba(255, 255, 255, 0.65);
  font-size: 12px;
  cursor: pointer;
  padding: 0 12px 0 calc(36px + var(--menu-depth, 0) * 14px);
  box-sizing: border-box;
  text-align: left;
  transition: all 120ms ease;
}

.admin-shell__submenu-item > span {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.admin-shell__submenu-item:hover {
  color: #ffffff;
  background: rgba(255, 255, 255, 0.05);
}

.admin-shell__submenu-item.active:not(.expandable) {
  color: #ffffff;
  background: var(--rr-color-primary);
  font-weight: 600;
}

.admin-shell__submenu-item.active:not(.expandable):hover {
  background: var(--rr-color-primary-hover, #4b39c7);
}

.admin-shell__submenu-item.expandable.active {
  color: #ffffff;
  background: transparent;
  font-weight: 600;
}

.admin-shell__submenu-item.expandable.active:hover {
  background: rgba(255, 255, 255, 0.05);
}

.admin-shell__submenu-item:disabled {
  opacity: 0.45;
  cursor: not-allowed;
  background: transparent;
}

.admin-shell__submenu-caret {
  flex-shrink: 0;
  opacity: 0.45;
  transition: transform 180ms ease, opacity 140ms ease;
}

.admin-shell__submenu-item:hover .admin-shell__submenu-caret,
.admin-shell__submenu-item.active .admin-shell__submenu-caret {
  opacity: 0.85;
}

.admin-shell__submenu-item.open .admin-shell__submenu-caret {
  transform: rotate(180deg);
}

.admin-shell__submenu-item small {
  flex-shrink: 0;
  padding: 1px 6px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.15);
  color: #ffffff;
  font-size: 10px;
}

.admin-shell__nav-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 32px 16px;
  color: rgba(255, 255, 255, 0.4);
  font-size: 12px;
}

/* Collapsed Sidebar Styles (Ant Design Pro standard) */
.admin-shell__sidebar.is-collapsed {
  width: var(--admin-shell-sidebar-width, 64px);
}

.admin-shell__sidebar.is-collapsed .admin-shell__nav-parent {
  grid-template-columns: 1fr;
  justify-items: center;
  height: 48px;
  padding: 4px 0;
}

.admin-shell__sidebar.is-collapsed .admin-shell__nav-icon {
  width: 40px;
  height: 40px;
  border-radius: var(--rr-radius-control);
  transition: all 120ms ease;
}

.admin-shell__sidebar.is-collapsed .admin-shell__nav-parent.active .admin-shell__nav-icon {
  background: var(--rr-color-primary);
  color: var(--rr-color-surface);
}

.admin-shell__sidebar.is-collapsed .admin-shell__nav-parent:hover:not(.active) .admin-shell__nav-icon {
  background: rgba(255, 255, 255, 0.1);
  color: #ffffff;
}

.admin-shell__sidebar.is-collapsed .admin-shell__nav-main,
.admin-shell__sidebar.is-collapsed .admin-shell__nav-caret,
.admin-shell__sidebar.is-collapsed .admin-shell__submenu {
  display: none !important;
}
</style>
